import React, { useState, useRef } from 'react';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Agent } from '../../types/agent-types-new';

interface AgentCardProps {
  agent: Agent;
  onClick?: () => void;
}

const NewAgentCard: React.FC<AgentCardProps> = ({ agent, onClick }) => {
  const [isHovering, setIsHovering] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Define maturity badge colors
  const maturityColor = {
    Rookie: 'bg-yellow-500',
    Senior: 'bg-blue-500',
    VP: 'bg-purple-500'
  }[agent.maturity];

  // Define progress bar colors
  const progressColor = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
    teal: 'bg-teal-500'
  }[agent.color || 'blue'];

  // Handle default avatar if none provided
  const avatarUrl = agent.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(agent.name)}&background=random`;

  // Mouse event handlers
  const handleMouseEnter = () => {
    setIsHovering(true);
    if (videoRef.current && videoRef.current.readyState >= 2) { // Only play if video is loaded
      videoRef.current.currentTime = 0;
      const playPromise = videoRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.warn("Video playback was prevented by browser:", err);
          // Don't show error in console as it's expected in some browsers
        });
      }
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    if (videoRef.current) {
      try {
        videoRef.current.pause();
      } catch (err) {
        // Ignore pause errors
      }
    }
  };

  // Check if this is a coming soon agent
  const isComingSoon = agent.marketplaceStatus === 'coming-soon';
  
  return (
    <div 
      className={`agent-card h-full flex flex-col rounded-lg border overflow-hidden cursor-pointer hover:shadow-md transition-all
        ${isComingSoon 
          ? 'bg-amber-50 border-amber-200' 
          : 'bg-white border-gray-200 shadow-sm'}`}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative">
        <div className={`absolute top-2 right-2 text-white text-xs font-bold px-2 py-1 rounded-full ${maturityColor} z-10`}>
          {agent.maturity}
        </div>
        <div className="aspect-square overflow-hidden relative">
          {/* Static image - shown when not hovering */}
          <img 
            src={avatarUrl}
            alt={agent.name}
            className={`agent-avatar object-cover w-full h-full transition-opacity duration-300 ${isHovering ? 'opacity-0 absolute' : 'opacity-100'}`}
          />
          
          {/* Video - shown when hovering */}
          {agent.actionVideo && (
            <video 
              ref={videoRef}
              className={`object-cover w-full h-full transition-opacity duration-300 ${isHovering ? 'opacity-100' : 'opacity-0 absolute'}`}
              muted
              playsInline
              loop
              preload="metadata"
            >
              <source src={agent.actionVideo} type="video/mp4" />
              {/* Fallback for browsers that don't support video */}
              Your browser does not support the video tag.
            </video>
          )}
          <div className="absolute inset-0 bg-black bg-opacity-10 transition-opacity"></div>
        </div>
      </div>
      <div className="p-4 flex-grow flex flex-col">
        <h3 className="font-bold text-lg mb-1">{agent.name}</h3>
        <p className="text-gray-600 text-sm mb-3 line-clamp-2 flex-grow">{agent.mission}</p>
        
        <div className="mt-auto">
          {typeof agent.progress === 'number' ? (
            <>
              <div className="flex justify-between items-center mb-1 text-sm">
                <span>Development</span>
                <span className="font-medium">{agent.progress}%</span>
              </div>
              <Progress
                value={agent.progress}
                className="h-2 mb-3"
              >
                <div
                  className={`h-full ${progressColor}`}
                  style={{ width: `${agent.progress}%` }}
                />
              </Progress>
            </>
          ) : (
            // No source records progress (PR #6323): no bar, no bare percent.
            <p className="text-sm text-muted-foreground italic mb-3">Development progress not recorded</p>
          )}
          
          <div className="flex items-center">
            <Badge variant="outline" className="text-xs">
              Current Challenge
            </Badge>
          </div>
          <p className="text-sm text-gray-700 mt-1 line-clamp-2">{agent.challenge || 'No current challenges'}</p>
        </div>
      </div>
      
      {/* Status indicator */}
      <div className="px-4 py-2 border-t flex justify-between items-center">
        <div className="flex items-center">
          <span className={`w-2 h-2 rounded-full mr-2 ${agent.status === 'active' ? 'bg-green-500' : agent.status === 'paused' ? 'bg-amber-500' : 'bg-gray-400'}`}></span>
          <span className="text-xs text-gray-600 capitalize">
            {agent.marketplaceStatus === 'coming-soon' ? (
              <span className="text-amber-600 font-semibold">Coming Soon</span>
            ) : (
              agent.status
            )}
          </span>
        </div>
        <Badge variant="secondary" className="text-xs">
          {agent.category}
        </Badge>
      </div>
    </div>
  );
};

export default NewAgentCard;